<script lang="ts" setup>
import AUApi from "@/api";
import { ref, onMounted, reactive } from "vue";
import { getKennungTitle, getKZValue } from "@/DataHelper";
import { Globals } from "@/Globals";
import { globalsStt, checkNode } from "@/local";
import { mountedBase, onSave, BaseSetup, onDelete } from "@/DataLoaderStatic";
import { getSiteData } from "@/NavigationHelper";
import { goBack } from "@/NavigationHelper";
import {
  createEditorValidator,
  provideEditorValidator,
} from "@/components/EditorValidator";
import { showSnackbar } from "@/UIHelper";
const validator = createEditorValidator();
provideEditorValidator(validator);

const emit = defineEmits(["back", "save"]);

let disabled = ref<boolean>(false);
let clearable = ref<boolean>(false);
let showDeleteMask: boolean = false;
let kostenart = ref<string>("");
let showEssen = ref<boolean>(false);
let showKredit = ref<boolean>(false);
let showBezahlt = ref<boolean>(false);
let showKst = ref<boolean>(false);
let saveVisible = ref<boolean>(true);
let showVollstaendig = ref<boolean>(true);
const siteData = getSiteData();
let showReceiptType = ref<boolean>(false);

let noChangeDate = ref<boolean>(false);
let isSaving = ref<boolean>(false);

const pageParams: DataPageParameter = {
  backOnSave: true,
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_NeuerSonstigerBeleg_MA",
  aic: AUApi.Current.user.aic,
  emitBack: () => emit("back"),
};
// Für Erfassung der Reisekosten Daten aus der Tabelle für die Sonstigen Belege vom MA ausgehend alt & KI!
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));
onMounted(async () => {
  const siteData = getSiteData();
  if (siteData.editQuery) {
    pageParams.query = siteData.editQuery;
    pageParams.mainAic = pageParams.aic;
  }

  if (siteData.aicReise) {
    showVollstaendig.value = false;
    // darf nur bei den sonstigen Belegen die auf den MA erfasst werden angezeigt werden!
    const Stamm = globalsStt(Globals.Stt, 0, "RK_TRAVEL");
    if (!pageParams.jokerStt) {
      pageParams.jokerStt = [];
    }
    pageParams.jokerStt.push({
      sync: siteData.aicReise, // aic Stammsatz
      aic: Stamm.aic, // aic Stammtyp
    });
  }
  if (siteData?.bewegungsdaten) {
    pageParams.bewegungsdaten = true;
    pageParams.aic = siteData.aicBewegung;
  } else {
    if (siteData.aicReise) {
      pageParams.bewegungsdaten = false;
      pageParams.aic = siteData.aicReise;
      pageParams.queryAIC = siteData.aicReise;
      pageParams.zeitbereich = siteData.zeitbereich;
    } else {
      pageParams.mainAic = AUApi.Current.user.aic;
      pageParams.mainStt = uiData.header.aic_Stamm;
    }
  }
  await mountedBase(uiData, pageParams, false);
  if (siteData.showDelete) {
    showDeleteMask = siteData.showDelete;
  }
  if (!uiData.showLoading && uiData.data) {
    const row = uiData.data[0];

    const bezahlt = getKennungTitle("RK_NOTPAYED", uiData.columns);
    if (bezahlt) {
      showBezahlt.value = true;
    }
    const kredit = getKennungTitle("RK_FIRMENKREDITKARTE", uiData.columns);
    if (kredit) {
      showKredit.value = true;
    }
    const essen = getKennungTitle("RK_ANZ_PERS", uiData.columns);
    if (essen) {
      showEssen.value = true;
    }

    const kostenstelle = getKennungTitle("COST_CENTRE", uiData.columns);
    if (kostenstelle) {
      showKst.value = true;
    }
    saveVisible.value = getKZValue(row, "save", uiData.columns);
    if (siteData.aicReise) {
      saveVisible.value = true;
    }
    if (siteData.aic) {
      showDeleteMask = saveVisible.value;
      disabled.value = !saveVisible.value;
      noChangeDate.value = disabled.value;
    }
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});
async function onValidate() {
  return await validator.validateAsync();
}
async function save() {
  if (isSaving.value) return;
  isSaving.value = true;
  const isvalid = await onValidate();
  if (!isvalid.valid) {
    showSnackbar({
      text:
        "Validierung fehlgeschlagen - Bitte prüfen! (" +
        isvalid.invalidFields.join(", ") +
        ")",
      duration: 4000,
      color: "error",
    });
    isSaving.value = false;
    return;
  }
  // achtung wird von den Reisen und vom MA direkt auch aufgerufen!
  const aic = siteData?.aicReise ?? AUApi.Current.user.aic;
  if (!siteData?.reloadData) {
    siteData.reloadData = true;
  }
  onSave(
    uiData,
    pageParams,
    0,
    "",
    pageParams.zeitbereich,
    aic,
    true,
    [],
    uiData.saveNodeRequest
  )
    ?.then(() => {
      emit("save");
    })
    .finally(() => {
      isSaving.value = false;
    });
}
async function bewDelete() {
  if (uiData.data && uiData.data[0].aic_Bew_pool) {
    await onDelete(uiData, pageParams, uiData.data[0].aic_Bew_pool, "");
    if (!siteData?.reloadData) {
      siteData.reloadData = true;
    }
  }
}
</script>
<template>
  <v-card :loading="uiData.showLoading" max-width="400px" class="noPaddingNoMargin">
    <!-- <Span> EditKostenersatzGrid</Span> -->
    <v-card-text v-if="uiData.initialized">
      <EditDatePicker
        :readonly="noChangeDate"
        :queryZeitbereich="pageParams.zeitbereich"
        kennung="VALIDITY"
      >
      </EditDatePicker>

      <EditComboBox
        v-model="kostenart"
        :readonly="disabled"
        kennung="RK_ARTEN"
        :queryZeitbereich="pageParams.zeitbereich"
      />
      <EditTextField
        v-if="showReceiptType"
        :clearable="clearable"
        kennung="RECEIPTTYPE"
        :readonly="disabled"
      />

      <EditComboBox
        :readonly="disabled"
        kennung="RK_GEBUEHRENREGION"
        :queryZeitbereich="pageParams.zeitbereich"
      />
      <EditWaehrung :readonly="disabled" class="pa-0" kennung="ALL_ROUND_PRICE" />
      <EditComboBox
        v-if="showKst"
        :readonly="disabled"
        kennung="COST_CENTRE"
        :queryZeitbereich="pageParams.zeitbereich"
      />

      <EditSlider
        :readonly="disabled"
        v-if="showEssen"
        class="pt-5"
        kennung="RK_ANZ_PERS"
      />
      <EditSwitchButton :readonly="disabled" v-if="showBezahlt" kennung="RK_NOTPAYED" />
      <EditSwitchButton
        :readonly="disabled"
        v-if="showKredit"
        kennung="RK_FIRMENKREDITKARTE"
      />

      <EditTextArea :clearable="clearable" kennung="MEMO" :readonly="disabled" />

      <EditFileChoicer :readonly="disabled" kennung="DOKUMENT_DATENBANK" />
      <EditSwitchButton
        :readonly="disabled"
        v-if="showVollstaendig"
        class="pt-1 pl-0"
        kennung="RK_SONSTIGERBELEG_VOLLSTAENDIG"
      />
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <CommonBaseButtons
        :deleteVisible="showDeleteMask"
        :createVisible="false"
        :refreshVisible="false"
        :backVisible="false"
        @delete="bewDelete"
        @back="goBack()"
        :saveVisible="saveVisible"
        @save="save"
      ></CommonBaseButtons>
    </v-card-actions>
  </v-card>
</template>
<style scoped>
.dvhCol {
  padding-right: 10px;
  padding-left: 10px;
  padding-top: 0px;
  padding-bottom: 0px;
}
</style>
